/* eslint-disable jsx-a11y/anchor-is-valid, no-param-reassign */
import React, { useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import _ from 'underscore';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { cloneDeep } from '@bingads-webui-universal/primitive-utilities';
import { FocusLock } from '@bingads-webui-react/focus-lock';
import { mergeStyles, FontIcon, registerIcons } from '@fluentui/react';
import { ViewSuggestionType, isViewSuggestionApplied, assetStrMap } from './ad-strength-helper';
import { getPickerListStyles, getLLMPickerListStyles } from './styles';
import { adStrengthSuggestionPerspectiveStrings } from './strings';
import llmIcon from '../media/llm.svg';

registerIcons({
  icons: {
    'llm-svg': (<img style={{ position: 'absolute', left: -4 }} src={llmIcon} alt="chatgpt-logo" />),
  },
});

const DKIThemeDefaultName = 'Asset_Theme_For_DKI';

export const AdStrengthSuggestionPicker = (props) => {
  const {
    i18n, target, suggestions, updateItem, hideSuggestionPicker, assetType, displayPostion,
    updateSuggestionMeasurement,
  } = props;

  const pickerListClass = useDefaultStyles(getPickerListStyles);
  const containerStyle = { position: 'absolute', left: `${displayPostion.left}px`, top: `${displayPostion.top}px` };

  useEffect(() => {
    const clickedAway = (event) => {
      if (!target.contains(event.target) &&
        event.target.closest('.ad-strength-suggestion-container') === null
      ) {
        hideSuggestionPicker();
        window.removeEventListener('click', clickedAway);
      }
    };
    const keydown = (event) => {
      switch (event.key) {
        case 'Escape': {
          const pickerContainer = document.querySelector('.ad-strength-suggestion-container');
          if (pickerContainer.hasChildNodes()) {
            event.stopPropagation();
            event.preventDefault();
            hideSuggestionPicker();
          }
          break;
        }
        default:
          break;
      }
    };

    if (!_.isEmpty(target)) {
      window.addEventListener('click', clickedAway);
      target.addEventListener('keydown', event => keydown(event));

      return () => {
        window.removeEventListener('click', clickedAway);
        target.removeEventListener('keydown', keydown);
      };
    }

    return () => { };
  }, [hideSuggestionPicker, target]);

  const addAssetSuggestion = (event, Asset, SuggestionId) => {
    event.stopPropagation();
    event.preventDefault();
    hideSuggestionPicker();

    if (!_.isEmpty(target)) {
      updateItem(Asset);
      updateSuggestionMeasurement({ applySuggestionIds: [SuggestionId] });
    }
  };

  const pickerTitle = assetType === ViewSuggestionType.Headline ?
    i18n.getString(_TL_('View recommended headlines')) :
    i18n.getString(_TL_('View recommended descriptions'));

  updateSuggestionMeasurement({
    viewSuggestionIds: _.map(suggestions, suggestion => suggestion.SuggestionId),
  });

  return (
    <React.Fragment>
      {
        suggestions.length > 0 ?
          <FocusLock
            onExit={hideSuggestionPicker}
            focusFirstItemOnMount
          >
            <ul className={classNames('picker-list', pickerListClass.root)} role="menu" style={containerStyle}>
              <li className={classNames('ad-strength-suggestion-picker-title', pickerListClass.pickerTitle)}>
                {pickerTitle}
              </li>
              {
                suggestions.map(({ SuggestionId, Asset }, index) => (
                  <li key={SuggestionId} role="menuitem">
                    <a
                      role="menuitem"
                      arial-label={Asset}
                      href="#"
                      onClick={event => addAssetSuggestion(event, Asset, SuggestionId)}
                      key={SuggestionId}
                      data-index={index}
                    >
                      {Asset || null}
                    </a>
                  </li>))
              }
            </ul>
          </FocusLock>
          : null
      }
    </React.Fragment>);
};

AdStrengthSuggestionPicker.propTypes = {
  suggestions: PropTypes.arrayOf({
    SuggestionId: PropTypes.string.isRequired,
    Asset: PropTypes.string.isRequired,
  }).isRequired,
  target: PropTypes.shape({
    addEventListener: PropTypes.func,
    removeEventListener: PropTypes.func,
    contains: PropTypes.func,
  }),
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  assetType: PropTypes.string.isRequired,
  updateItem: PropTypes.func.isRequired,
  hideSuggestionPicker: PropTypes.func.isRequired,
  displayPostion: PropTypes.shape({
    left: PropTypes.number.isRequired,
    top: PropTypes.number.isRequired,
  }).isRequired,
  updateSuggestionMeasurement: PropTypes.func.isRequired,
};

AdStrengthSuggestionPicker.defaultProps = {
  target: null,
};

export const AdStrengthLLMSuggestionPicker = (props) => {
  const {
    target, suggestions, perspectives, updateItem, showSuggestionPicker, hideSuggestionPicker, displayPostion,
    updateSuggestionMeasurement, autoFocus,
  } = props;

  const pickerRef = useRef(null);
  const pickerListClass = useDefaultStyles(getLLMPickerListStyles);
  const containerStyle = {
    position: 'absolute',
    left: `${displayPostion.left}px`,
    top: `${displayPostion.top}px`,
    maxHeight: 180,
    overflowY: 'auto',
    border: '1px solid #B3B0AD',
    borderRadius: 2,
  };

  useEffect(() => {
    const clickedAway = (event) => {
      if (!target.contains(event.target) &&
        event.target.closest('.ad-strength-suggestion-container') === null
      ) {
        hideSuggestionPicker();
        window.removeEventListener('click', clickedAway);
      }
    };
    const keydown = (event) => {
      switch (event.key) {
        case 'Escape':
        case 'Tab': {
          const pickerContainer = document.querySelector('.ad-strength-suggestion-container');
          if (pickerContainer.hasChildNodes()) {
            if (event.key === 'Escape') {
              event.stopPropagation();
              event.preventDefault();
            }
            hideSuggestionPicker();
          }
          break;
        }
        case 'ArrowDown': {
          event.stopPropagation();
          event.preventDefault();
          showSuggestionPicker(event);
          break;
        }
        default:
          break;
      }
    };

    if (!_.isEmpty(target)) {
      window.addEventListener('click', clickedAway);
      target.addEventListener('keydown', event => keydown(event));

      return () => {
        window.removeEventListener('click', clickedAway);
        target.removeEventListener('keydown', keydown);
      };
    }

    return () => { };
  }, [hideSuggestionPicker, showSuggestionPicker, target]);

  useEffect(() => {
    if (pickerRef.current && autoFocus) {
      const firstItem = pickerRef.current.querySelector('[role="menuitem"]');
      if (firstItem) {
        firstItem.focus();
      }
    }
    if (pickerRef.current) {
      const keydown = (event) => {
        switch (event.key) {
          case 'Escape':
          case 'Tab': {
            hideSuggestionPicker();
            if (target) {
              target.focus();
            }
            break;
          }
          default:
            break;
        }
      };
      const pickerTarget = pickerRef.current;
      pickerTarget.addEventListener('keydown', event => keydown(event));
      return () => {
        pickerTarget.removeEventListener('keydown', keydown);
      };
    }
    return () => { };
  }, [autoFocus, hideSuggestionPicker, pickerRef, target]);

  const addAssetSuggestion = (event, Asset, SuggestionId) => {
    event.stopPropagation();
    event.preventDefault();
    hideSuggestionPicker();

    if (!_.isEmpty(target)) {
      target.focus();
      updateItem(Asset);
      updateSuggestionMeasurement({ applySuggestionIds: [SuggestionId] });
    }
  };

  updateSuggestionMeasurement({
    viewSuggestionIds: _.map(suggestions, suggestion => suggestion.SuggestionId),
  });

  const iconClass = mergeStyles({
    position: 'absolute',
    top: 12,
    left: 12,
  });

  const perspectiveItems = _.map(perspectives, (perspective) => {
    const filteredPerspectiveSuggestions = _.filter(suggestions, suggestion =>
      suggestion.Perspective.toLowerCase() === perspective.Perspective.toLowerCase());

    const suggestionItems = _.map(filteredPerspectiveSuggestions, (suggestion, index) => (
      <li key={suggestion.SuggestionId}>
        <a
          role="menuitem"
          className="ad-strength-suggestion-picker-item"
          aria-label={suggestion.Asset}
          href="#"
          onClick={event => addAssetSuggestion(event, suggestion.Asset, suggestion.SuggestionId)}
          key={suggestion.SuggestionId}
          data-index={index}
        >
          {suggestion.Asset || null}
        </a>
      </li>));

    return (
      suggestionItems.length > 0 ?
        <ul className={classNames('picker-list', pickerListClass.root)} role="presentation">
          <FontIcon iconName="llm-svg" className={iconClass} role="none" />
          <li className={classNames('ad-strength-suggestion-picker-title', pickerListClass.pickerTitle)} role="presentation">
            {perspective.Perspective}
          </li>
          {suggestionItems}
        </ul> :
        null
    );
  });

  return (
    <React.Fragment>
      {
        suggestions.length > 0 ?
          <div className="ad-strength-llm-suggestion-picker" style={containerStyle} ref={pickerRef} role="menu">
            {perspectiveItems}
          </div>
          : null
      }
    </React.Fragment>);
};

AdStrengthLLMSuggestionPicker.propTypes = {
  suggestions: PropTypes.arrayOf({
    SuggestionId: PropTypes.string.isRequired,
    Asset: PropTypes.string.isRequired,
  }).isRequired,
  target: PropTypes.shape({
    addEventListener: PropTypes.func,
    removeEventListener: PropTypes.func,
    contains: PropTypes.func,
    focus: PropTypes.func,
  }),
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  updateItem: PropTypes.func.isRequired,
  showSuggestionPicker: PropTypes.func.isRequired,
  hideSuggestionPicker: PropTypes.func.isRequired,
  displayPostion: PropTypes.shape({
    left: PropTypes.number.isRequired,
    top: PropTypes.number.isRequired,
  }).isRequired,
  updateSuggestionMeasurement: PropTypes.func.isRequired,
  perspectives: PropTypes.arrayOf({
    Perspective: PropTypes.string.isRequired,
  }).isRequired,
  autoFocus: PropTypes.bool,
};

AdStrengthLLMSuggestionPicker.defaultProps = {
  target: null,
  autoFocus: false,
};

function filterLLMSuggestion(suggestions, assetTypeString, value, assets) {
  const filteredTypeSuggestions = _.filter(suggestions.Suggestions, suggestion =>
    suggestion.AssetType.toLowerCase() === assetTypeString &&
    !_.isEmpty(suggestion.Asset) &&
    !isViewSuggestionApplied(assets, suggestion.Asset) &&
    !(!_.isEmpty(value) && (!suggestion.Asset.toLowerCase().startsWith(value.toLowerCase()) || suggestion.Asset.toLowerCase() === value.toLowerCase())));

  return filteredTypeSuggestions;
}

function getDisplayPosition(target, container) {
  // Calculate the position of the popup relative to the container.
  // The container needs to be non-statically positioned, either fixed, relative or absolute
  const targetRect = target.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  return {
    top: (targetRect.bottom - containerRect.top) + 1,
    left: targetRect.left - containerRect.left - 1,
  };
}

export const useAdStrengthSuggestion = ({
  container,
  i18n,
  ref,
  llmAssetsSuggestions,
  assets,
  assetType,
  updateItem,
  isForVnext,
  index,
  updateSuggestionMeasurement,
  enableDKI,
  DKIThemeName,
}) => {
  const assetTypeString = assetStrMap[assetType];
  const containerElement = document.querySelector(container);

  if (containerElement && containerElement.querySelectorAll('.ad-strength-suggestion-container').length === 0) {
    const div = document.createElement('div');
    div.className = 'ad-strength-suggestion-container open';
    containerElement.appendChild(div);
  }

  const hideSuggestionPicker = useCallback(() => {
    if (containerElement) {
      const pickerContainer = containerElement.querySelector('.ad-strength-suggestion-container');
      if (pickerContainer) {
        ReactDOM.unmountComponentAtNode(pickerContainer);
      }
    }
  }, [containerElement]);

  useEffect(() => {
    const target = ref.current;
    let displayPostion = {};
    if (containerElement && target) {
      displayPostion = getDisplayPosition(target, containerElement);
    }

    const showSuggestionPicker = (event) => {
      if (event && event.type === 'keydown' && event.key === 'Escape') {
        return;
      }

      if (!containerElement) {
        return;
      }

      const assetsCopy = cloneDeep(assets);
      assetsCopy[index].Asset.Text = target.value;

      if (_.isEmpty(llmAssetsSuggestions)) { return; }

      if (llmAssetsSuggestions.ModelName === 'Fallback' && !_.isEmpty(llmAssetsSuggestions.Suggestions)) {
        llmAssetsSuggestions.Perspectives = [
          {
            Perspective: assetType === ViewSuggestionType.Headline ?
              adStrengthSuggestionPerspectiveStrings.Ad_Strength_Suggestion_Perspective_Headline_Title :
              adStrengthSuggestionPerspectiveStrings.Ad_Strength_Suggestion_Perspective_Description_Title,
            PitchingPoint: assetType === ViewSuggestionType.Headline ?
              adStrengthSuggestionPerspectiveStrings.Ad_Strength_Suggestion_Perspective_Headline_Pitching_Point :
              adStrengthSuggestionPerspectiveStrings.Ad_Strength_Suggestion_Perspective_Description_Pitching_Point,
          },
        ];
        _.each(llmAssetsSuggestions.Suggestions, (suggestion) => {
          // eslint-disable-next-line no-param-reassign
          suggestion.Perspective = assetType === 'headline' ?
            adStrengthSuggestionPerspectiveStrings.Ad_Strength_Suggestion_Perspective_Headline_Title :
            adStrengthSuggestionPerspectiveStrings.Ad_Strength_Suggestion_Perspective_Description_Title;
        });
      }

      let filteredSuggestions = filterLLMSuggestion(llmAssetsSuggestions, assetTypeString, target.value, assetsCopy);
      let perspectives = llmAssetsSuggestions.Perspectives.sort(perspective => perspective.Perspective);

      if (enableDKI) {
        const DKIfilteredSuggestions = filteredSuggestions.filter(suggestion => suggestion.Perspective === DKIThemeDefaultName).slice(0, 2).map(suggestion => ({ ...suggestion, Perspective: i18n.getString(DKIThemeName) }));
        const normalfilteredSuggestions = filteredSuggestions.filter(suggestion => suggestion.Perspective !== DKIThemeDefaultName);
        filteredSuggestions = [...DKIfilteredSuggestions, ...normalfilteredSuggestions];
        perspectives=[{ Perspective: i18n.getString(DKIThemeName), PitchingPoint: '' }, ...perspectives.filter(perspective => perspective.Perspective !== DKIThemeDefaultName)];
      }
      const autoFocus = event && event.type === 'keydown' && event.key === 'ArrowDown';

      if (filteredSuggestions.length > 0) {
        ReactDOM.render(<AdStrengthLLMSuggestionPicker
          i18n={i18n}
          target={target}
          suggestions={filteredSuggestions}
          perspectives={perspectives}
          assets={assets}
          assetType={assetTypeString}
          updateItem={updateItem}
          value={target.value}
          showSuggestionPicker={showSuggestionPicker}
          hideSuggestionPicker={hideSuggestionPicker}
          displayPostion={displayPostion}
          updateSuggestionMeasurement={updateSuggestionMeasurement}
          autoFocus={autoFocus}
        />, containerElement.querySelector('.ad-strength-suggestion-container'));
      } else {
        hideSuggestionPicker();
      }
    };

    if (!_.isEmpty(target) && (assetType === 'Headlines' || assetType === 'Descriptions') && isForVnext) {
      if (target === document.activeElement) {
        showSuggestionPicker();
      }
      target.addEventListener('input', showSuggestionPicker);
      target.addEventListener('click', showSuggestionPicker);
      target.addEventListener('keydown', showSuggestionPicker);

      return () => {
        target.removeEventListener('input', showSuggestionPicker);
        target.removeEventListener('click', showSuggestionPicker);
        target.removeEventListener('keydown', showSuggestionPicker);
      };
    }

    return () => { };
  }, [assetType, assetTypeString, assets, containerElement, hideSuggestionPicker, i18n, index, isForVnext, llmAssetsSuggestions, ref, updateItem, updateSuggestionMeasurement]);
};
